/* global console */

import type { CapturedError } from "./error-reporting";
import { addErrorSink, removeErrorSink } from "./error-reporting";
import { redactDeep, redactString } from "./redact";

/**
 * Crash reporting for the task pane.
 *
 * Mirrors the main web app (frontend-legal app/plugins/sentry.ts), with one
 * rule above all others:
 *
 *   Sentry is a NON-SOVEREIGN vendor. Firms in Australian mode (au_limiter, a
 *   per-team admin setting every member inherits) must never have data sent to
 *   it.
 *
 * That gate FAILS CLOSED. Anything other than a clear, explicit "this user is
 * not in Australian mode" means no reporting. A missing, malformed, or
 * still-loading value is treated as Australian mode: the cost of wrongly
 * staying quiet is a lost crash report, the cost of wrongly sending is a
 * data-residency breach.
 */

export interface SentryUser {
  id?: string;
  email?: string;
  /** Firm-wide Australian mode. Anything but an explicit false denies. */
  au_limiter?: boolean | string | number;
}

/**
 * May we send this user's data to Sentry?
 *
 * Deliberately strict. The flag comes from a JSON response, so it can arrive as
 * the string "true"/"false" or as 1/0 rather than a boolean; only a value that
 * unambiguously means "not in Australian mode" passes.
 */
export function isSentryAllowedForUser(user: SentryUser | null | undefined): boolean {
  if (user == null) return false;

  const flag = user.au_limiter;
  if (flag === false) return true;
  if (typeof flag === "string") return flag.trim().toLowerCase() === "false";
  if (typeof flag === "number") return flag === 0;

  // true, undefined, null, or anything unexpected -> deny.
  return false;
}

/** Scrub an outgoing Sentry event, including where the SDK puts the message. */
export function scrubEvent<T>(event: T): T {
  const e = event as unknown as Record<string, unknown>;
  if (e == null || typeof e !== "object") return event;

  // The SDK stores a captured error's text under exception.values[].value. The
  // first version of this scrubber never looked there, so a bearer token inside
  // an error message left the browser untouched.
  redactDeep(e.exception);
  redactDeep(e.request);
  redactDeep(e.extra);
  redactDeep(e.contexts);
  redactDeep(e.breadcrumbs);
  redactDeep(e.tags);
  if (typeof e.message === "string") e.message = redactString(e.message);
  return event;
}

/**
 * Which policy decision the current client was started under. Bumped on every
 * start/stop so a slow dynamic import cannot finish and begin reporting after
 * the policy has already changed underneath it.
 */
let generation = 0;
/**
 * Which generation owns the currently-started client, or null if none.
 *
 * A plain boolean was not enough. Two sign-ins in quick succession attach two
 * continuations to the same in-flight import; the stale one resolves first and
 * would set the flag back to false AFTER the newer one had already started a
 * client — leaving Sentry running while the module believed it was stopped, so
 * a later stop skipped its cleanup and a later start re-initialised from
 * scratch. Tying the state to the generation removes the ambiguity.
 */
let startedGeneration: number | null = null;

/** Start crash reporting, if and only if this user is allowed one. */
export async function initSentry(user: SentryUser | null | undefined): Promise<void> {
  if (startedGeneration !== null) return;

  const dsn = process.env.SENTRY_DSN;
  if (dsn == null || dsn === "") return;
  if (process.env.APP_ENV !== "production") return;
  if (!isSentryAllowedForUser(user)) return;

  const myGeneration = generation;
  startedGeneration = myGeneration;

  try {
    const Sentry = await import("@sentry/browser");

    // The policy can change while the import is in flight — a logout, or a
    // different user signing in. Re-check before doing anything at all.
    if (myGeneration !== generation || !isSentryAllowedForUser(user)) {
      // Only stand down if WE are the generation that claimed it. A newer call
      // may already have started a client legitimately.
      if (startedGeneration === myGeneration) startedGeneration = null;
      return;
    }

    Sentry.init({
      dsn,
      environment: process.env.APP_ENV,
      release: process.env.APP_VERSION,
      // Nothing automatic, and `integrations: []` alone does NOT achieve that:
      // it leaves every default in place. Verified against the SDK — with an
      // empty array you still get Breadcrumbs, GlobalHandlers, BrowserSession
      // and eight more. Only `defaultIntegrations: false` clears them.
      //
      // This matters enormously here. The console breadcrumb integration
      // records every console argument, and this app logs AI responses to legal
      // questions (e.g. useSubmitFeedback logs the whole response object). That
      // is client content, and it must never reach a third party. GlobalHandlers
      // would also send raw errors straight past our capture-time redaction.
      //
      // Errors reach Sentry ONLY through our own sink below, already sanitised.
      defaultIntegrations: false,
      integrations: [],
      // No client reports, no session envelopes, no automatic PII.
      sendClientReports: false,
      sendDefaultPii: false,
      beforeSend: (event) => scrubEvent(event),
      denyUrls: [/.*node_modules.*/],
    });

    // Id only. An email address is personal data and the id is enough to group
    // a customer's reports.
    if (user?.id != null) Sentry.setUser({ id: user.id });

    addErrorSink("sentry", (entry: CapturedError) => {
      // Entries are already sanitised at capture; re-checking the generation
      // means an in-flight error cannot slip out after a policy change.
      if (myGeneration !== generation) return;
      const err = new Error(entry.message);
      err.stack = entry.stack;
      Sentry.captureException(err, {
        tags: { area: entry.area ?? "unknown", host: entry.host ?? "unknown" },
        extra: { componentStack: entry.componentStack, at: entry.at },
      });
    });
  } catch (error) {
    if (startedGeneration === myGeneration) startedGeneration = null;
    console.error("Could not start crash reporting:", error);
  }
}

/**
 * Stop sending to Sentry immediately.
 *
 * Removing the sink is synchronous and happens FIRST, so nothing further can be
 * sent even though closing the client is asynchronous. Bumping the generation
 * also disarms any initialisation still in flight.
 */
export function stopSentry(): void {
  generation += 1;
  // Unconditional and synchronous: this is the actual residency gate, so it
  // must not depend on any state that a race could get wrong.
  removeErrorSink("sentry");
  if (startedGeneration === null) return;
  startedGeneration = null;

  void (async () => {
    try {
      const Sentry = await import("@sentry/browser");
      Sentry.setUser(null);
      await Sentry.getClient()?.close();
    } catch (error) {
      console.error("Could not stop crash reporting:", error);
    }
  })();
}
